'use client'

import React, { useRef, useState } from 'react'

import { useAutoResizeTextarea } from './useAutoResizeTextarea'
import { useResizableTextarea } from './useResizableTextarea'

import { Box, HTMLChakraProps, chakra } from '@chakra-ui/react'

import { ResizeHandleIcon } from '../Icon/ResizeHandleIcon'
import {
  ColorScheme,
  actionButtonInnerStyles,
  actionButtonWrapperStyles,
  multilineContainerStyles,
  multilineLabelStyles,
  multilineOuterStyles,
  multilineTextareaStyles,
  multilineWarningTextStyles,
  resizeHandleStyles,
  resizedTextareaStyles,
  textareaWrapperStyles,
} from './MultilineTextInput.styles'

export type { ColorScheme }

type OwnedTextareaProps =
  | 'value'
  | 'onChange'
  | 'onFocus'
  | 'onBlur'
  | 'disabled'
  | 'autoFocus'
  | 'placeholder'
  | 'width'

export type MultilineTextInputProps = Omit<
  HTMLChakraProps<'textarea'>,
  OwnedTextareaProps
> & {
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  colorScheme?: ColorScheme
  width?: string | number
  autoFocus?: boolean
  autoResize?: boolean
  autoResizeBoundaryRef?: React.RefObject<HTMLElement>
  autoResizeMaxRows?: number
  autoResizeViewportOffset?: number
  actionButton?: React.ReactNode
  label?: string
  rightLabel?: string
  isDisabled?: boolean
  /** Renders the corner drag handle that lets the operator grow the field. */
  isResizable?: boolean
  outerPadding?: string | number
  hasSideBorders?: boolean
  /** Paints the error border and reveals `warningText`. */
  hasWarning?: boolean
  warningText?: string
}

export const MultilineTextInput = ({
  value,
  onChange,
  placeholder,
  colorScheme = 'dark',
  width = '100%',
  autoFocus,
  autoResize = false,
  autoResizeBoundaryRef,
  autoResizeMaxRows,
  autoResizeViewportOffset,
  actionButton,
  label,
  rightLabel,
  isDisabled = false,
  isResizable = true,
  outerPadding = '20px',
  hasSideBorders = true,
  hasWarning = false,
  warningText,
  ...rest
}: MultilineTextInputProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const shouldShowResizeHandle = isResizable && !isDisabled
  const {
    height: resizedHeight,
    isResizing,
    handleProps,
  } = useResizableTextarea({ enabled: shouldShowResizeHandle, textareaRef })

  useAutoResizeTextarea({
    boundaryRef: autoResizeBoundaryRef,
    // A dragged height is the operator's explicit choice, so auto-resize steps
    // aside for the rest of this input's life.
    enabled: autoResize && resizedHeight === null,
    hasManualHeight: resizedHeight !== null,
    maxRows: autoResizeMaxRows,
    textareaRef,
    value,
    viewportOffset: autoResizeViewportOffset,
  })

  const input = (
    <Box
      {...multilineContainerStyles(colorScheme, isFocused, width, hasWarning)}
      opacity={isDisabled ? 0.5 : 1}
    >
      <Box {...textareaWrapperStyles}>
        <chakra.textarea
          autoFocus={autoFocus}
          disabled={isDisabled}
          onBlur={() => setIsFocused(false)}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          ref={textareaRef}
          value={value}
          {...multilineTextareaStyles(colorScheme)}
          {...rest}
          style={{ ...rest.style, ...resizedTextareaStyles(resizedHeight) }}
        />
        {shouldShowResizeHandle && (
          <Box
            aria-label='Resize text input'
            aria-orientation='horizontal'
            data-testid='multilineTextInput_resizeHandle'
            role='separator'
            tabIndex={0}
            {...resizeHandleStyles(
              colorScheme,
              isResizing,
              !!actionButton || (hasWarning && !!warningText)
            )}
            {...handleProps}
          >
            <ResizeHandleIcon />
          </Box>
        )}
      </Box>
      <Box
        {...actionButtonWrapperStyles(!!actionButton)}
        pointerEvents={isDisabled ? 'none' : undefined}
      >
        <Box {...actionButtonInnerStyles}>{actionButton}</Box>
      </Box>
      {hasWarning && warningText && (
        <Box
          alignSelf='flex-start'
          {...multilineWarningTextStyles(colorScheme)}
        >
          {warningText}
        </Box>
      )}
    </Box>
  )

  if (!label) return input

  return (
    <Box
      {...multilineOuterStyles(colorScheme, hasSideBorders)}
      p={outerPadding}
    >
      <Box alignItems='center' display='flex' justifyContent='space-between'>
        <Box as='span' {...multilineLabelStyles(colorScheme)}>
          {label}
        </Box>
        {rightLabel && (
          <Box as='span' {...multilineLabelStyles(colorScheme)}>
            {rightLabel}
          </Box>
        )}
      </Box>
      {input}
    </Box>
  )
}
